"use client";

// Form
import { Button, ButtonVariant, Checkbox, Input, Text, TextColor, TextSize, TextVariant, TextWeight } from "@workspace/ui/components";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useCustomer } from "@/context/CustomerContext/CustomerContext";
import { useGlobal } from "@/context/GlobalContext/GlobalContext";
import validationSchema from "./validationSchema";
const ContactForm = () => {
  const [status, setStatus] = useState({
    unauthorized: false,
    success: false
  });
  const formRef = useRef(null);
  const recaptchaRef = useRef(null);
  const {
    state: {
      customer
    }
  } = useCustomer();
  useEffect(() => {
    if (customer) {
      formRef.current?.setFieldValue("email", customer.email);
      formRef.current?.setFieldValue("firstName", customer.firstname);
    }
  }, [customer]);
  const {
    getContactFormTokenData,
    sendContactFormMessageData
  } = useGlobal();

  // InitialValues
  const initialValues = {
    firstName: "",
    email: "",
    message: "",
    terms: false,
    recaptcha: null
  };
  const formSchema = validationSchema();
  const statusContent = {
    unauthorized: "Nie udało się wysłać wiadomości",
    success: "Wiadomość została wysłana"
  };
  const handleRecaptchaChange = (token, setFieldValue) => {
    setFieldValue("recaptcha", token);
  };
  const handleSubmit = async (values, {
    setSubmitting,
    setFieldError,
    resetForm
  }) => {
    try {
      // Dla invisible reCAPTCHA - wykonaj weryfikację przed wysłaniem
      if (recaptchaRef.current && !values.recaptcha) {
        try {
          const token_0 = await recaptchaRef.current.executeAsync();
          if (token_0) {
            values.recaptcha = token_0;
          } else {
            throw new Error("reCAPTCHA verification failed");
          }
        } catch (recaptchaError) {
          console.error("reCAPTCHA error:", recaptchaError);
          setFieldError("recaptcha", "Weryfikacja reCAPTCHA nie powiodła się. Spróbuj ponownie.");
          setSubmitting(false);
          return;
        }
      }
      const response = await getContactFormTokenData();
      if (response && response.token && response.code === 200) {
        const contactFormToken = response.token;
        if (contactFormToken && contactFormToken !== null) {
          const responseSend = await sendContactFormMessageData(values.email, values.message, contactFormToken);
          if (responseSend && responseSend.code === 200) {
            setStatus({
              unauthorized: false,
              success: true
            });
            toast.success("Wiadomość została wysłana");
            // Reset formularza i reCAPTCHA
            resetForm();
            if (recaptchaRef.current) {
              recaptchaRef.current.reset();
            }
          } else {
            setStatus({
              unauthorized: true,
              success: false
            });
          }
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({
        unauthorized: true,
        success: false
      });
    } finally {
      setSubmitting(false);
    }
  };
  return <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={handleSubmit} innerRef={formRef}>
      {({
      values: values_0,
      touched,
      errors,
      setFieldValue: setFieldValue_0,
      isSubmitting
    }) => <ContactFormWrapper className="py-6">
          {Object.values(status).indexOf(true) > -1 && <InfoBar $error={status.unauthorized} $success={status.success}>
              <Text variant={TextVariant.P} text={Object.values(statusContent)[Object.values(status).indexOf(true)]} color={status.success ? TextColor.SPECIAL_700 : TextColor.ERROR_500} />
            </InfoBar>}

          <FormWrapper>
            <div className="flex flex-col gap-2 mb-8">
              <Text text="Kontakt" variant={TextVariant.H1} fontSize={TextSize.XXL} weight={TextWeight.BOLD} />

              <Text text="Masz pytania? Wybierz interesujący Cię temat i skontaktuj się z nami, używając poniższego formularza" variant={TextVariant.P} fontSize={TextSize.S} color={TextColor.GRAYSCALE_600} />
            </div>

            <div className="flex gap-2">
              <Input id="firstName" value={values_0.firstName} name="firstName" label="Imię" placeholder="Wprowadź imię" focused={touched.firstName} validated={typeof errors.firstName === "undefined" && touched.firstName} error={typeof errors.firstName !== "undefined" && touched.firstName} errorHelper={errors.firstName} onChange={e => setFieldValue_0("firstName", e.target.value)} icon={false} type="text" autoComplete="off" />

              <Input id="email" value={values_0.email} name="email" label="Email" placeholder="Wprowadź email" focused={touched.email} validated={typeof errors.email === "undefined" && touched.email} error={typeof errors.email !== "undefined" && touched.email} errorHelper={errors.email} onChange={e_0 => setFieldValue_0("email", e_0.target.value)} icon={false} type="text" autoComplete="off" />
            </div>

            <div className="flex gap-2">
              <Input id="message" value={values_0.message} name="message" label="Wiadomość" placeholder="Wprowadź wiadomość" focused={touched.message} validated={typeof errors.message === "undefined" && touched.message} error={typeof errors.message !== "undefined" && touched.message} errorHelper={errors.message} onChange={e_1 => setFieldValue_0("message", e_1.target.value)} icon={false} type="text" multiline rows={4} autoComplete="off" />
            </div>

            <div className="flex flex-col gap-2 items-start w-full relative mb-2">
              <Checkbox id="terms" name="terms" label="" labelHtml="Zapoznałem się z <a href='/regulamin-sklepu' target='_blank'>regulaminem</a> oraz <a href='/polityka-prywatnosci' target='_blank'>polityką prywatności</a> i akceptuję jego postanowienia. <span className='text-red-500'>*</span>" checked={values_0.terms} onChange={() => setFieldValue_0("terms", !values_0.terms)} justifyStart />

              <Text text="Ta strona jest chroniona przez reCAPTCHA. Dane osobowe zbierane i przetwarzane są w celu udzielenia odpowiedzi na skierowane przez formularz pytania." color={TextColor.GRAYSCALE_600} fontSize={TextSize.XS} className="max-w-[450px] text-center mx-auto" />

              {typeof errors.terms !== "undefined" && touched.terms && <ErrorFormHelperText>
                  <Text text={errors.terms} color={TextColor.ERROR_500} fontSize={TextSize.XS} />
                </ErrorFormHelperText>}
            </div>

            <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "6LcMQacUAAAAAIPS6AzgoyiIC4ehbiF4w73diOeK"} size="invisible" onChange={token_1 => handleRecaptchaChange(token_1, setFieldValue_0)} onExpired={() => setFieldValue_0("recaptcha", null)} onError={() => setFieldValue_0("recaptcha", null)} />

            <ButtonsWrapper>
              <Button type="submit" variant={ButtonVariant.PRIMARY} text={isSubmitting ? "Weryfikacja..." : "Wyślij wiadomość"} disabled={isSubmitting} loading={isSubmitting} />
            </ButtonsWrapper>
          </FormWrapper>
        </ContactFormWrapper>}
    </Formik>;
};

// Usuń niepotrzebne styled components dla reCAPTCHA
const ErrorFormHelperText = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  color: ${({
  theme
}) => theme.colors.ERROR_500} !important;
  line-height: 20px;
  text-align: right !important;
`;
const InfoBar = styled.div`
  width: 100%;
  margin-bottom: 20px;
  text-align: center;
  padding: 2px 10px;

  ${({
  theme,
  $error
}) => $error && `
    background-color: ${theme.colors.ERROR_400};
  `}

  ${({
  theme,
  $success
}) => $success && `
    background-color: ${theme.colors.SPECIAL_300};
  `}
`;
const FormWrapper = styled(Form)`
  display: flex;
  flex-direction: column;

  .MuiFormGroup-root {
    margin-bottom: 0;
    margin-right: 16px;
    width: 100%;
  }

  button {
    width: 100%;
  }

  .MuiFormGroup-root {
    margin-bottom: 16px;
  }
`;
const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;

  button {
    width: fit-content;
    padding: 8px 32px;
  }

  ${({
  theme
}) => theme.mediaQueries.sm} {
    flex-direction: column-reverse;
    justify-content: center;
  }
`;
const ContactFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100% !important;
  max-width: 900px;

  ${({
  theme
}) => theme.mediaQueries.sm} {
    max-width: 100%;
  }
`;
export default ContactForm;